import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, LogOut } from "lucide-react";
import { ApiError } from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { formatDateTime, roleLabel } from "../lib/format";
import { Alert, Button, Card, Field, Input, PageHeader } from "../components/ui";

export function ProfilePage() {
  const { user, changePassword, logoutNow } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword !== confirmPassword) {
      setError("Konfirmasi kata sandi baru tidak sama.");
      return;
    }

    setBusy(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setSuccess("Kata sandi berhasil diubah. Semua sesi lain telah dicabut.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Kata sandi gagal diubah.");
    } finally {
      setBusy(false);
    }
  }

  async function onLogout(): Promise<void> {
    try {
      await logoutNow();
    } finally {
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Profil" description="Informasi akun dan keamanan kata sandi." />

      <Card className="space-y-3">
        <h2 className="font-semibold text-slate-800">Informasi akun</h2>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Detail label="Nama lengkap" value={user?.fullName ?? "-"} />
          <Detail label="Username" value={user?.username ?? "-"} />
          <Detail label="Email" value={user?.email ?? "-"} />
          <Detail label="Peran" value={user ? roleLabel(user.role) : "-"} />
          <Detail label="Nomor induk" value={user?.identifierNumber ?? "-"} />
          <Detail label="Masuk terakhir" value={formatDateTime(user?.lastLoginAt)} />
        </dl>
        <div className="pt-1">
          <Button variant="secondary" onClick={onLogout}>
            <LogOut className="size-4" aria-hidden />
            Keluar dari perangkat ini
          </Button>
        </div>
      </Card>

      <Card className="space-y-4">
        <div>
          <h2 className="font-semibold text-slate-800">Ubah kata sandi</h2>
          <p className="mt-1 text-sm text-slate-600">
            Minimal 10 karakter, memuat huruf besar, huruf kecil, dan angka.
          </p>
        </div>

        <form className="grid gap-3 sm:max-w-md" onSubmit={onSubmit}>
          <Field label="Kata sandi saat ini" htmlFor="currentPassword">
            <Input
              id="currentPassword"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
            />
          </Field>

          <Field label="Kata sandi baru" htmlFor="newPassword">
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
            />
          </Field>

          <Field label="Ulangi kata sandi baru" htmlFor="confirmPassword">
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />
          </Field>

          {error ? <Alert tone="error">{error}</Alert> : null}
          {success ? <Alert tone="success">{success}</Alert> : null}

          <Button type="submit" loading={busy}>
            <KeyRound className="size-4" aria-hidden />
            Simpan kata sandi baru
          </Button>
        </form>
      </Card>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-sm font-medium text-slate-800">{value}</dd>
    </div>
  );
}
